import {
  Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, ElementRef, OnChanges, SimpleChanges, TemplateRef, DestroyRef, inject, AfterViewInit, ViewEncapsulation, HostBinding, contentChildren, computed, DOCUMENT,
} from '@angular/core'
import {
  NavigationEnd, Router,
} from '@angular/router'
import { filter } from 'rxjs'
import { SiteNavigationIntl } from './site-navigation.intl'
import { CommonModule } from '@angular/common'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon'
import {
  RouterOrExternalLinkDirective, TemplateTypeDirective,
} from '@knorr-bremse-portals/ngx-components'
import {
  iconArrowsRight, iconArrowRight, iconArrowLeft, iconBurgerMenu, iconClose,
} from '@knorr-bremse-portals/icons'
import { NavigationContentComponent } from './navigation-content/navigation-content.component'

export declare type NavigationLevel = NavigationItem[]
export interface NavigationItem {
  label: string
  href?: string
  target?: string
  children?: NavigationLevel
  contentId?: string
}

declare type ExtendedNavigationLevel = ExtendedNavigationItem[]
interface ExtendedNavigationItem extends NavigationItem {
  active: 'child' | 'exact' | null
  children?: ExtendedNavigationLevel
  parentItem?: ExtendedNavigationItem
}

const clientScript = function (hostElement: HTMLElement) {
  const closeOverlay = function () {
    const activeElement = document.activeElement
    if (activeElement instanceof HTMLElement) {
      activeElement.blur() // needed for mobile navigation, especially safari
    }

    // on desktop we need to shortly force the overlay to be closed to remove hover
    hostElement.classList.add('closed')
    let timeout: ReturnType<typeof setTimeout> | null = null
    const eventFunction = () => {
      if (timeout !== null) {
        clearTimeout(timeout)
      }
      hostElement.removeEventListener('mouseleave', eventFunction)

      hostElement.classList.remove('closed')
    }
    timeout = setTimeout(eventFunction, 500) // this is used as a fallback for mobile navigation
    hostElement.addEventListener('mouseleave', eventFunction, { once: true }) // this makes desktop class removal faster
  }

  const scrollHorizontalToElement = function (element: HTMLElement, scrollParent: HTMLElement) {
    const elementRect = element.getBoundingClientRect()
    const scrollParentRect = scrollParent.getBoundingClientRect()
    const scrollOffset = {
      left: elementRect.left - scrollParentRect.left,
      right: scrollParentRect.right - elementRect.right,
    }
    if (scrollOffset.left < 0 || scrollOffset.right < 0) {
      const scrollLeft = scrollParent.scrollLeft + Math.min(scrollOffset.left, 0) - Math.min(scrollOffset.right, 0)
      scrollParent.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      })
    }
  }

  const getHorizontalScrollContainer = function (element: HTMLElement): HTMLElement | null {
    const navLevelElement = element.closest<HTMLElement>('.nav-level')
    if (navLevelElement === null) {
      return null
    }

    if (window.getComputedStyle(navLevelElement).overflow !== 'visible' && navLevelElement.clientWidth !== navLevelElement.scrollWidth) {
      return navLevelElement
    }

    const parentElement = navLevelElement.parentElement
    if (parentElement === null) {
      return null
    }

    return getHorizontalScrollContainer(parentElement)
  }

  let lastScrollRightHandlerPair: [HTMLElement, () => void] | null = null

  const scrollLevelRight = function (event: Event) {
    const navItemElement = event.currentTarget as HTMLElement

    const navItemContainerElement = navItemElement.parentElement as HTMLElement

    const childLevelElement = navItemContainerElement.querySelector<HTMLElement>('.nav-level')
    if (childLevelElement === null) { // The nav-item does not have children
      return
    }

    if (!getIsMobile()) {
      // We first need to wait for the child navigation to actually appear
      if (lastScrollRightHandlerPair !== null) {
        lastScrollRightHandlerPair[0].removeEventListener('transitionend', lastScrollRightHandlerPair[1])
      }
      const scrollRightFunction = () => {
        const scrollContainerElement = getHorizontalScrollContainer(navItemContainerElement)
        if (scrollContainerElement === null) {
          return
        }
        scrollHorizontalToElement(childLevelElement, scrollContainerElement)
      }
      lastScrollRightHandlerPair = [childLevelElement, scrollRightFunction]
      childLevelElement.addEventListener('transitionend', scrollRightFunction, { once: true })
    } else {
      if (event.type === 'mouseenter') {
        return
      }

      const scrollContainerElement = getHorizontalScrollContainer(navItemContainerElement)
      if (scrollContainerElement === null) {
        return
      }
      scrollHorizontalToElement(childLevelElement, scrollContainerElement)
    }
  }

  const onNavItemClick = function (event: Event) {
    const navItemElement = event.currentTarget as HTMLElement

    const isMobile = getIsMobile()
    const hasLink = navItemElement.getAttribute('href') !== null

    if (!isMobile) {
      if (hasLink) {
        closeOverlay()
      }
      return
    }

    const hasChildren = navItemElement.parentElement?.querySelector('.nav-level')
    if (hasChildren) {
      event.preventDefault()
      event.stopPropagation()
      scrollLevelRight(event)
    } else {
      closeOverlay()
    }
  }

  const breadcrumpScroll = function (event: Event) {
    const breadcrumbItemElement = event.currentTarget as HTMLElement

    event.stopPropagation()

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const backLevels = parseInt(breadcrumbItemElement.getAttribute('back-levels')!)

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let targetNavItemContainerElement = breadcrumbItemElement.closest<HTMLElement>('.nav-item-container')!
    for (let level = 0; level < backLevels; level++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      targetNavItemContainerElement = targetNavItemContainerElement.parentElement!.closest<HTMLElement>('.nav-item-container')!
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const navItemElement = targetNavItemContainerElement.querySelector<HTMLElement>(':scope > .nav-item')!
    navItemElement.focus({
      preventScroll: true,
    })
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const navListElement = targetNavItemContainerElement.closest<HTMLElement>('.nav-list')!

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    scrollHorizontalToElement(navListElement, getHorizontalScrollContainer(targetNavItemContainerElement)!)
  }

  const getIsMobile = function (): boolean {
    const mobileButtonElement = hostElement.querySelector('.mobile-overlay-button')
    if (mobileButtonElement === null) {
      return false
    }
    return window.getComputedStyle(mobileButtonElement).display !== 'none'
  }

  const attachEventListeners = function () {
    for (const element of hostElement.querySelectorAll('.nav-item')) {
      element.removeEventListener('click', onNavItemClick)
      element.addEventListener('click', onNavItemClick)
    }

    for (const element of hostElement.querySelectorAll('.nav-item')) {
      element.removeEventListener('mouseenter', scrollLevelRight)
      element.removeEventListener('focusin', scrollLevelRight)
      element.addEventListener('mouseenter', scrollLevelRight)
      element.addEventListener('focusin', scrollLevelRight)
    }

    for (const element of hostElement.querySelectorAll('.breadcrumb-item')) {
      element.removeEventListener('click', breadcrumpScroll)
      element.addEventListener('click', breadcrumpScroll)
    }
  }

  const observer = new MutationObserver(attachEventListeners)
  observer.observe(hostElement, {
    childList: true,
    subtree: true,
  })

  attachEventListeners()
}

const trailingSlashesRegex = /\/+$/
const leadingSlashesRegex = /^\/+/
function trimSlashes(path: string): string {
  return path.replace(trailingSlashesRegex, '').replace(leadingSlashesRegex, '')
}

function navigationExactMatch(navigationItem: NavigationItem, currentPath: string): boolean {
  return (navigationItem.href !== undefined && trimSlashes(navigationItem.href) === currentPath)
}

function getPath(url: string): string {
  return (new URL(url, 'https://fakebase')).pathname
}

export function normalizeToPath(url: string): string {
  return decodeURI(trimSlashes(getPath(url)))
}

export function getActiveNavigation(navigationItem: NavigationLevel, url: string): ({
  type: 'exact' | 'longest'
  navigation: NavigationLevel
}) {
  const normalizedPath = normalizeToPath(url)
  let activeNavigation = getActiveNavigationExactMatch(navigationItem, normalizedPath)
  if (activeNavigation !== false) {
    return {
      type: 'exact',
      navigation: activeNavigation.reverse(),
    }
  }

  activeNavigation = getActiveNavigationLongestMatch(navigationItem, normalizedPath, [])
  if (activeNavigation !== false) {
    return {
      type: 'longest',
      navigation: activeNavigation.reverse(),
    }
  }

  return {
    type: 'exact',
    navigation: [],
  }
}

function getActiveNavigationExactMatch(navigationLevel: NavigationLevel, activePath: string): (NavigationLevel | false) {
  for (const navigationItem of navigationLevel) {
    if (navigationExactMatch(navigationItem, activePath)) {
      return [navigationItem]
    }

    if (navigationItem.children) {
      const exactMatchPath = getActiveNavigationExactMatch(navigationItem.children, activePath)
      if (exactMatchPath !== false) {
        exactMatchPath.push(navigationItem)
        return exactMatchPath
      }
    }
  }

  return false
}

function getActiveNavigationLongestMatch(navigationLevel: NavigationLevel, activePath: string, currentPath: NavigationLevel): (NavigationLevel | false) {
  let longestMatchLength = 0
  let longestMatchPath: NavigationItem[] | null = null

  for (const navigationItem of navigationLevel) {
    const newPath = [navigationItem, ...currentPath]

    if (navigationItem.href && activePath.startsWith(trimSlashes(navigationItem.href)) && navigationItem.href.length > longestMatchLength) {
      longestMatchLength = navigationItem.href.length
      longestMatchPath = newPath
    }

    if (navigationItem.children) {
      const longestMatch = getActiveNavigationLongestMatch(navigationItem.children, activePath, newPath)
      if (longestMatch !== false) {
        return longestMatch
      }
    }
  }

  if (longestMatchPath) {
    return longestMatchPath
  }

  return false
}

@Component({
  selector: 'nav[kb-site-navigation]',
  templateUrl: './site-navigation.component.html',
  styleUrls: ['./site-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, IconComponent, RouterOrExternalLinkDirective, TemplateTypeDirective],
})
export class SiteNavigationComponent implements OnChanges, AfterViewInit {
  @HostBinding('attr.tabindex') private tabindex = '0' // Safari does not apply :focus-within without tabindex

  protected iconOverviewMenuEntry = iconArrowsRight
  protected iconSubMenu = iconArrowRight
  protected iconBack = iconArrowLeft
  protected iconMobileMenuClosed = iconBurgerMenu
  protected iconMobileMenuOpen = iconClose

  protected navLevelTemplateType!: {
    navigationParentItem: ExtendedNavigationItem
  }

  protected navItemTemplateType!: {
    navigationItem: ExtendedNavigationItem
  }

  protected breadcrumpsTemplateType!: {
    navigationItem: ExtendedNavigationItem
    backLevels: number
  }

  @Input({ required: true }) public navigation!: NavigationLevel
  protected extendedNavigation!: ExtendedNavigationLevel

  private navigationContent = contentChildren(NavigationContentComponent)
  protected navigationContentMap = computed(() => {
    const navigationContentMap = new Map<string, TemplateRef<unknown>>()
    this.navigationContent().forEach((contentTemplate) => {
      navigationContentMap.set(contentTemplate.id(), contentTemplate.content())
    })
    return navigationContentMap
  })

  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)
  private router = inject(Router)
  protected intl = inject(SiteNavigationIntl)
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef)
  private document = inject(DOCUMENT)

  public constructor() {
    this.setupActiveNavigationItemHandler()
    this.intl.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck())
  }

  public ngAfterViewInit(): void {
    this.setupClientScript()
  }

  private setupClientScript(): void {
    if (this.document.getElementById('site-navigation-client-script') !== null) {
      clientScript(this.hostElement.nativeElement)
      return
    }

    const scriptElement = this.document.createElement('script')
    scriptElement.id = 'site-navigation-client-script'

    const getHostElement = function () {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return document.querySelector<HTMLElement>('nav[kb-site-navigation]')!
    }

    scriptElement.innerHTML = `(${clientScript.toString()})((${getHostElement.toString()})())`

    this.document.body.appendChild(scriptElement)
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (Object.hasOwn(changes, 'navigation')) {
      this.updateExtendedNavigation(this.navigation)
      this.updateNavigationItemsActivated(this.router.url)
    }
  }

  private updateExtendedNavigation(navigationLevel: NavigationLevel) {
    const createExtendedNavigationLevel = (navigationLevel: NavigationLevel, parentNavigationItem?: ExtendedNavigationItem): ExtendedNavigationLevel => {
      const extendedNavigationLevel: ExtendedNavigationLevel = []
      for (const navigationItem of navigationLevel) {
        const extendedNavigationItem: ExtendedNavigationItem = {
          label: navigationItem.label,
          href: navigationItem.href,
          target: navigationItem.target,
          active: null,
          contentId: navigationItem.contentId,
        }
        if (navigationItem.children) {
          extendedNavigationItem.children = createExtendedNavigationLevel(navigationItem.children, extendedNavigationItem)
        }
        if (parentNavigationItem) {
          extendedNavigationItem.parentItem = parentNavigationItem
        }
        extendedNavigationLevel.push(extendedNavigationItem)
      }
      return extendedNavigationLevel
    }
    this.extendedNavigation = createExtendedNavigationLevel(navigationLevel)
  }

  private setupActiveNavigationItemHandler() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((event) => {
      this.updateNavigationItemsActivated(event.urlAfterRedirects)
    })
  }

  private updateNavigationItemsActivated(url: string) {
    this.resetNavigationItems(this.extendedNavigation)
    this.activateNavigationItems(this.extendedNavigation, normalizeToPath(url))
    this.cdr.markForCheck()
  }

  private resetNavigationItems(navigationLevel: ExtendedNavigationLevel) {
    for (const navigationItem of navigationLevel) {
      if (navigationItem.children) {
        this.resetNavigationItems(navigationItem.children)
      }
      navigationItem.active = null
    }
  }

  private activateNavigationItems(navigationLevel: ExtendedNavigationLevel, currentPath: string): void {
    const activeNavigation = getActiveNavigation(navigationLevel, currentPath)
    const navigationLength = activeNavigation.navigation.length
    for (let i = 0; i < navigationLength; i++) {
      const activeItem = activeNavigation.navigation[i];
      (activeItem as ExtendedNavigationItem).active = (i === navigationLength - 1 && activeNavigation.type === 'exact') ? 'exact' : 'child'
    }
  }
}
